import { useEffect, useMemo, useRef, useState } from 'react'

import { allGroupItems } from './slash-command'
import type { SlashCommandGroup, SlashCommandItem } from './slash-command'
import type { SlashKeyHandler } from './use-slash-command'

/** Next selectable index in `delta` direction, stepping over disabled
 *  items; stays put when none remains in that direction. */
function stepEnabled(
  items: SlashCommandItem[],
  from: number,
  delta: number,
): number {
  for (let i = from + delta; i >= 0 && i < items.length; i += delta) {
    if (!items[i].disabled) return i
  }
  return from
}

function firstEnabled(items: SlashCommandItem[]): number {
  const index = items.findIndex((item) => !item.disabled)
  return index === -1 ? 0 : index
}

interface NavSnapshot {
  items: SlashCommandItem[]
  activeIndex: number
}

interface NavActions {
  setActive: (index: number) => void
  select: (item: SlashCommandItem) => void
  dismiss: () => void
}

function handleKey(
  event: KeyboardEvent,
  snap: NavSnapshot,
  actions: NavActions,
): boolean {
  if (event.key === 'ArrowDown') {
    actions.setActive(stepEnabled(snap.items, snap.activeIndex, 1))
    return true
  }
  if (event.key === 'ArrowUp') {
    actions.setActive(stepEnabled(snap.items, snap.activeIndex, -1))
    return true
  }
  if (event.key === 'Enter') {
    const item = snap.items[snap.activeIndex] as SlashCommandItem | undefined
    if (item && !item.disabled) actions.select(item)
    return true
  }
  if (event.key === 'Escape') {
    actions.dismiss()
    return true
  }
  return false
}

export interface SlashNav {
  activeIndex: number
  setActive: (index: number) => void
}

/** Owns the slash menu's single flat selection list + keyboard handler. */
export function useSlashNav({
  groups,
  query,
  keyRef,
  onSelect,
  onDismiss,
}: {
  groups: SlashCommandGroup[]
  query: string
  keyRef: React.RefObject<SlashKeyHandler>
  onSelect: (item: SlashCommandItem) => void
  onDismiss: () => void
}): SlashNav {
  const items = useMemo(() => allGroupItems(groups), [groups])
  const [activeIndex, setActiveIndex] = useState(0)

  // Re-highlight the first selectable match whenever the filter query or
  // the option set changes.
  useEffect(() => {
    setActiveIndex(firstEnabled(items))
  }, [query, items])

  const snapRef = useRef<NavSnapshot>(null as never)
  snapRef.current = { items, activeIndex }
  const actionsRef = useRef<NavActions>(null as never)
  actionsRef.current = {
    setActive: setActiveIndex,
    select: onSelect,
    dismiss: onDismiss,
  }

  useEffect(() => {
    keyRef.current = (event) =>
      handleKey(event, snapRef.current, actionsRef.current)
    return () => {
      keyRef.current = () => false
    }
  }, [keyRef])

  return { activeIndex, setActive: setActiveIndex }
}
